"use client";

import { useCallback, useLayoutEffect, useRef, useState } from "react";

/**
 * Track an `<img>` / next-`Image` element's loaded state so we can drive an
 * opacity-fade safely — including when the browser resolves the image from
 * cache *before* React attaches the `onLoad` handler. A layout effect inspects
 * `complete` after mount and updates state before the browser paints, so
 * cached images render at their final opacity on the first frame (no flash on
 * back/forward navigation where components remount with `loaded=false`).
 *
 * Returns `[loaded, refCallback, onLoad]` — spread `refCallback` onto the
 * element's `ref` prop and `onLoad` onto its `onLoad` prop.
 */
export const useImageLoaded = () => {
	const [loaded, setLoaded] = useState(false);
	const imgRef = useRef<HTMLImageElement | null>(null);

	const refCallback = useCallback((node: HTMLImageElement | null) => {
		imgRef.current = node;
	}, []);

	useLayoutEffect(() => {
		const node = imgRef.current;
		if (node?.complete && node.naturalHeight > 0) setLoaded(true);
	}, []);

	const onLoad = useCallback(() => setLoaded(true), []);
	return [loaded, refCallback, onLoad] as const;
};
